import React, { useCallback, useState, useMemo, useEffect } from 'react';
import Joi from 'joi';
import Big from 'big.js';

import { useSmallScreen } from 'utils/hooks';
import { validateSchema, getPartialPaymentsTotal } from 'utils/helpers';
import { EURO_SYMBOL } from 'utils/constants';
import { texts } from 'utils/languages/fr';

import { ActionButton, CancelButton, CustomDialogTitle, CustomTextField } from 'components';
import { Dialog, DialogActions, DialogContent, InputAdornment } from '@mui/material';

import makeStyles from '@mui/styles/makeStyles';
import { useCreatePartialPaymentMutation } from 'api/main';

const useStyles = makeStyles(theme => ({
    dialogContent: {
        padding: theme.spacing(2, 3),
        color: theme.palette.grey[600],
        width: '100%',
    },
}));

const CreditDialog = ({ open, onClose, data, filters }) => {
    const fullScreen = useSmallScreen();
    const [amount, setAmount] = useState('');
    const [error, setError] = useState('');
    const classes = useStyles();

    const [createPartialPayment, { isLoading: loading, isSuccess }] =
        useCreatePartialPaymentMutation();

    const partialPaymentMax = useMemo(() => {
        if (!data) return 0;
        const partialTotal = getPartialPaymentsTotal(data);
        const bigAmount = new Big(data.amount);
        const bigTotal = new Big(partialTotal);
        return bigAmount.minus(bigTotal).toNumber();
    }, [data]);

    const schema = useMemo(
        () => ({
            amount: Joi.number().greater(0).max(partialPaymentMax).required().messages({
                'number.base': texts.AMOUNT_INVALID,
                'number.greater': texts.AMOUNT_INVALID,
                'number.max': texts.CREDIT_AMOUNT_MAX,
            }),
        }),
        [partialPaymentMax]
    );

    const handleClose = useCallback(() => {
        setAmount('');
        onClose();
    }, [onClose]);

    const handlePartialPayment = useCallback(() => {
        const { valid, newErrors } = validateSchema(schema, { amount });
        if (valid) {
            setError('');
            createPartialPayment({ payment: data, amount });
        } else {
            setError(newErrors.amount);
        }
    }, [data, createPartialPayment, amount, schema]);

    useEffect(() => {
        if (isSuccess) {
            handleClose();
        }
    }, [isSuccess, handleClose]);

    return (
        <Dialog fullScreen={fullScreen} open={open}>
            <CustomDialogTitle title={texts.CREDIT_PAYMENT} handleClose={handleClose} />
            <DialogContent className={classes.dialogContent}>
                <CustomTextField
                    fullWidth
                    formatNumber
                    error={error}
                    value={amount}
                    label={texts.CREDIT_AMOUNT_LABEL}
                    onChange={e => setAmount(e.target.value)}
                    endAdornment={<InputAdornment position="start">{EURO_SYMBOL}</InputAdornment>}
                />
            </DialogContent>
            <DialogActions>
                <CancelButton onClick={handleClose}>{texts.CANCEL}</CancelButton>
                <ActionButton loading={loading} disabled={loading} onClick={handlePartialPayment}>
                    {texts.VALIDER}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default CreditDialog;
